import type {MouseEvent, ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {Button, Size, Type} from '@Components/button';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE, ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import {UploadsMediaType, USER_UPLOADS_MEDIA_TABS} from '@Panels/user-media-panel/user-media-panel.types';
import {useSelectedTab} from '@Components/tabs';
import styles from './record-dropdown.module.scss';
import {useAppSelector} from '@/hooks';

export enum RECORD_DROPDOWN_OPTIONS {
  PHOTO = 'PHOTO',
  VIDEO = 'VIDEO',
  AUDIO = 'AUDIO',
}

interface RecordDropdownProps {
  onClickOption(id: RECORD_DROPDOWN_OPTIONS): void;
  onClose?: () => void;
  isSmallVersion?: boolean;
  mediaTypesToShow: Array<UploadsMediaType>;
  customClasses?: string;
  panelId?: string;
  defaultTab: UploadsMediaType;
  children?: ReactElement;
}

export function RecordDropdown({customClasses = '', isSmallVersion = false, ...props}: RecordDropdownProps): ReactElement | null {
  const [recordPopupShown, setRecordPopupShown] = useState(false);
  const itemsToShow = useRef<number>(3);
  const activeTab = useSelectedTab(USER_UPLOADS_MEDIA_TABS, props.defaultTab) as UploadsMediaType;
  const currentCollectionId = useAppSelector((state) => {
    return state.mediaUploads.collection;
  });
  const isForDesignUpload = useAppSelector((state) => {
    return state.mediaUploads.isForDesignUpload;
  });

  const onClickHandler = (id: RECORD_DROPDOWN_OPTIONS, e: MouseEvent): void => {
    e.stopPropagation();
    props.onClickOption(id);
  };

  const getItems = (): Array<ControlledListItemType> => {
    const items: Array<ControlledListItemType> = [];
    if (props.mediaTypesToShow.indexOf(UploadsMediaType.IMAGE) !== -1) {
      items.push({
        id: RECORD_DROPDOWN_OPTIONS.PHOTO,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_capture_photo'),
        icon: 'icon-camera',
        listSize: ControlledListItemSize.MEDIUM,
      });
    }

    if (props.mediaTypesToShow.indexOf(UploadsMediaType.VIDEO) !== -1) {
      items.push({
        id: RECORD_DROPDOWN_OPTIONS.VIDEO,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_record_video'),
        icon: 'icon-videocam',
        listSize: ControlledListItemSize.MEDIUM,
      });
    }

    if (props.mediaTypesToShow.indexOf(UploadsMediaType.AUDIO) !== -1) {
      items.push({
        id: RECORD_DROPDOWN_OPTIONS.AUDIO,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_record_audio'),
        icon: 'icon-microphone',
        listSize: ControlledListItemSize.MEDIUM,
      });
    }

    itemsToShow.current = items.length;
    return items;
  };

  const getRecordPopup = (): ReactElement => {
    return <ControlledList className={customClasses} onClick={onClickHandler} items={getItems()} />;
  };

  const getRecordSelector = (): ReactElement => {
    return (
      props.children ?? (
        <Button
          onClick={() => {
            return setRecordPopupShown(true);
          }}
          key="record-and-upload-media"
          customClasses={`${customClasses} ${recordPopupShown ? styles.recordDropdownSelectorClicked : ''} ${isSmallVersion ? '_full-width' : ''}`}
          type={Type.SECONDARY}
          size={isSmallVersion ? Size.MEDIUM : Size.SMALL}
          icon="icon-radio-button-checked"
          text={isForDesignUpload ? window.i18next.t('pmwjs_record') : window.i18next.t('pmwjs_record_media')}
        />
      )
    );
  };

  const getHeightClasses = (): string => {
    switch (itemsToShow.current) {
      case 1:
        return styles.singleItem;
      case 2:
        return styles.twoItems;
      default:
        return '';
    }
  };

  const prepareButtonForRecord = (text: string, type: RECORD_DROPDOWN_OPTIONS): ReactElement => {
    return (
      <Button
        onClick={(e: React.MouseEvent) => {
          onClickHandler(type, e);
        }}
        key="record-and-upload-media"
        customClasses={`spacing-m-l-4 ${customClasses} ${recordPopupShown ? styles.recordDropdownSelectorClicked : ''} ${isSmallVersion ? '_full-width' : ''}`}
        type={Type.SECONDARY}
        size={isSmallVersion ? Size.MEDIUM : Size.SMALL}
        icon="icon-radio-button-checked"
        text={text}
      />
    );
  };

  const getButtonAccordingToSelectedTab = (): ReactElement | null => {
    switch (activeTab) {
      case UploadsMediaType.AUDIO:
        return prepareButtonForRecord(window.i18next.t('pmwjs_record_audio'), RECORD_DROPDOWN_OPTIONS.AUDIO);
      case UploadsMediaType.IMAGE_BACKGROUND:
        return prepareButtonForRecord(window.i18next.t('pmwjs_capture_photo'), RECORD_DROPDOWN_OPTIONS.PHOTO);
      case UploadsMediaType.IMAGE:
        return prepareButtonForRecord(window.i18next.t('pmwjs_capture_photo'), RECORD_DROPDOWN_OPTIONS.PHOTO);
      case UploadsMediaType.VIDEO:
        return prepareButtonForRecord(window.i18next.t('pmwjs_record_video'), RECORD_DROPDOWN_OPTIONS.VIDEO);
      default:
        return null;
    }
  };

  const getRecordDropdown = (): ReactElement | null => {
    if (currentCollectionId !== '') {
      return getButtonAccordingToSelectedTab();
    }
    return (
      <Dropdown
        onOpen={() => {
          return setRecordPopupShown(true);
        }}
        onClose={() => {
          return setRecordPopupShown(false);
        }}
        modalTitle={window.i18next.t('pmwjs_record')}
        showModalForSmallScreen={isSmallVersion}
        popupSpacingClass="spacing-p-3"
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        popupClassName={`${styles.popup} ${customClasses} ${getHeightClasses()}`}
        popUpHasCustomWidth
        popUpHasCustomHeight
        popupOffset={10.5}
        stopClickPropagation
        className={isSmallVersion ? '_full-width' : 'spacing-m-l-4'}
        selector={getRecordSelector()}
        popup={getRecordPopup()}
      />
    );
  };

  return getRecordDropdown();
}
